// Per-service checklist templates — ported verbatim from gas/Checklists.js.
// Used by apiSupabase.createBooking to generate checklist_tasks in Supabase
// (parity with the legacy GAS createChecklist). Data block is a faithful copy;
// tasksForService() ports the comma-split + fuzzy service matcher.

// The interior pass, named once because TWO services run it end to end:
// "Interior Detail" and "Extraction +". Shared rather than copied so a task
// added to the interior list can't silently skip the extraction job — the
// drift this file already warns about further down, in miniature.
// ⚠️ Signature / Pro deliberately DON'T use this: they interleave the same
// tasks with their exterior pass in their own order.
const _INTERIOR_TASKS = [
  "Gather and tag personal belongings",
  "Vacuum",
  "Clean surfaces",
  "Glass",
  "Door jams",
  "Final vacuum",
  "Mats",
  "Once over",
  "Put personal belongings back",
];

export const SERVICE_CHECKLISTS = {
  "Interior Detail": [..._INTERIOR_TASKS],
  "Exterior Detail": [
    "Wheels",
    "Engine bay",
    "Touchless wash",
    "Contact wash",
    "Coating",
    "Dry",
    "Door jams",
    "Once over",
  ],
  "Signature Service": [
    "Gather and tag personal belongings",
    "Vacuum",
    "Clean surfaces",
    "Glass",
    "Door jams",
    "Final vacuum",
    "Put personal belongings back",
    "Wheels",
    "Engine bay",
    "Touchless wash",
    "Contact wash",
    "Dry",
    "Mats",
    "Once over",
  ],
  "Pro Service": [
    "Gather and tag personal belongings",
    "Vacuum",
    "Clean surfaces",
    "Glass",
    "Door jams",
    "Final vacuum",
    "Once over",
    "Put personal belongings back",
    "Wheels",
    "Engine bay",
    "Touchless wash",
    "Contact wash",
    "Coating",
    "Dry",
    "Exterior door jams",
    "Mats",
    "Exterior once over",
  ],
  "Extraction": [
    "Extraction",
  ],
  // Extraction + is the FULL interior with an extraction in it (Ryder,
  // 2026-09-26), not a lighter stand-alone job — which is what the old
  // "Ex. & Wipe" name described and why this was only two lines. Extraction
  // sits straight after the vacuum, the order it's actually worked in: dry
  // debris out first, then wet, then surfaces and glass over clean carpet.
  // ⚠️ The old "Wipe Down" line is gone — `Clean surfaces` from the interior
  // pass is the same job, and two tasks meaning one thing on one card is how a
  // checklist stops being trusted.
  "Extraction +": [
    "Gather and tag personal belongings",
    "Vacuum",
    "Extraction",
    ..._INTERIOR_TASKS.filter((t) => t !== "Gather and tag personal belongings" && t !== "Vacuum"),
  ],
  // Parent services (Ryder, 2026-09-26). Backseat Overhaul is the monthly
  // member visit — rear cabin only, a month of kids. New Parent Prep is the
  // one-time deep clean before a newborn rides; the cabin filter is a part
  // and is billed as its own invoice line, not absorbed into the package.
  "Backseat Overhaul": [
    "Remove car seats & rear debris",
    "Vacuum rear seats & floors",
    "Seat-back pockets",
    "Spot-treat stains",
    "Wipe rear doors, cup holders & seat-backs",
    "Rear glass & touch points",
    "Reinstall car seats",
    "Final inspection",
  ],
  "New Parent Prep": [
    "Gather and tag personal belongings",
    "Full vacuum — seams & rails",
    "Extraction — seats & carpets",
    "Steam / sanitize all surfaces",
    "Air vents & ventilation",
    "Replace cabin air filter (bill the part)",
    "Headliner",
    "Door jams",
    "Cup holders & crevices",
    "Glass",
    "Odor treatment",
    "Put personal belongings back",
    "Final inspection",
  ],
  "Ceramic Coating": [
  "Wash vehicle",
  "Clay bar treatment",
  "Buff — front bumper",
  "Buff — rear bumper",
  "Buff — driver fender",
  "Buff — passenger fender",
  "Buff — hood",
  "Buff — roof",
  "Buff — trunk",
  "Buff — driver door",
  "Buff — driver rear door",
  "Buff — passenger door",
  "Buff — passenger rear door",
  "Buff — driver pillar",
  "Buff — passenger pillar",
  "Buff — driver rocker",
  "Buff — passenger rocker",
  "Buff — driver quarter panel",
  "Buff — passenger quarter panel",
  "Buff — driver mirror",
  "Buff — passenger mirror",
  "Polish — front bumper",
  "Polish — rear bumper",
  "Polish — driver fender",
  "Polish — passenger fender",
  "Polish — hood",
  "Polish — roof",
  "Polish — trunk",
  "Polish — driver door",
  "Polish — driver rear door",
  "Polish — passenger door",
  "Polish — passenger rear door",
  "Polish — driver pillar",
  "Polish — passenger pillar",
  "Polish — driver rocker",
  "Polish — passenger rocker",
  "Polish — driver quarter panel",
  "Polish — passenger quarter panel",
  "Polish — driver mirror",
  "Polish — passenger mirror",
  "Surface Prep",
  "Ceramic Coating",
],
  // Seasonal Ceramic Coating (S.C.C.): panel-aware like Ceramic Coating, but
  // ONLY wash + a single polish pass per panel (no buff, no surface-prep/coat
  // steps). tasksForServiceAndVehicle rebuilds the Polish lines from the real
  // vehicle's panels; this base is the sedan fallback.
  "Seasonal Ceramic Coating": [
  "Wash vehicle",
  "Polish — front bumper",
  "Polish — hood",
  "Polish — driver fender",
  "Polish — passenger fender",
  "Polish — roof",
  "Polish — driver door",
  "Polish — driver rear door",
  "Polish — passenger door",
  "Polish — passenger rear door",
  "Polish — driver rocker",
  "Polish — passenger rocker",
  "Polish — driver quarter panel",
  "Polish — passenger quarter panel",
  "Polish — trunk",
  "Polish — rear bumper",
  "Polish — driver mirror",
  "Polish — passenger mirror",
],
  "2-Step Paint Correction": [
  "Wash",
  "Clay bar",
  "Buff — front bumper",
  "Buff — rear bumper",
  "Buff — driver fender",
  "Buff — passenger fender",
  "Buff — hood",
  "Buff — roof",
  "Buff — trunk",
  "Buff — driver door",
  "Buff — driver rear door",
  "Buff — passenger door",
  "Buff — passenger rear door",
  "Buff — driver pillar",
  "Buff — passenger pillar",
  "Buff — driver rocker",
  "Buff — passenger rocker",
  "Buff — driver quarter panel",
  "Buff — passenger quarter panel",
  "Buff — driver mirror",
  "Buff — passenger mirror",
  "Polish — front bumper",
  "Polish — rear bumper",
  "Polish — driver fender",
  "Polish — passenger fender",
  "Polish — hood",
  "Polish — roof",
  "Polish — trunk",
  "Polish — driver door",
  "Polish — driver rear door",
  "Polish — passenger door",
  "Polish — passenger rear door",
  "Polish — driver pillar",
  "Polish — passenger pillar",
  "Polish — driver rocker",
  "Polish — passenger rocker",
  "Polish — driver quarter panel",
  "Polish — passenger quarter panel",
  "Polish — driver mirror",
  "Polish — passenger mirror"
  ],
  "Trim & Plastic Restoration": [
  "Clean trim surfaces",
  "Apply trim restorer",
  "Buff and finish",
  "Final wipe down",
],
"Avant Guard Formula 1051": [
  "Wash",
  "Clay bar",
  "Buff — front bumper",
  "Buff — rear bumper",
  "Buff — driver fender",
  "Buff — passenger fender",
  "Buff — hood",
  "Buff — roof",
  "Buff — trunk",
  "Buff — driver door",
  "Buff — driver rear door",
  "Buff — passenger door",
  "Buff — passenger rear door",
  "Buff — driver pillar",
  "Buff — passenger pillar",
  "Buff — driver rocker",
  "Buff — passenger rocker",
  "Buff — driver quarter panel",
  "Buff — passenger quarter panel",
  "Buff — driver mirror",
  "Buff — passenger mirror",
  "Surface prep",
  "Apply Formula 1051",
  "Cure check",
  "Final inspection",
],
"Strip and Shine - Ground": [
  "Pre-wash inspection",
  "Mask brake calipers",
  "Strip old dye/coating from wheel face",
  "Decontaminate wheel face",
  "Surface prep",
  "Apply recoat to wheel face",
  "Apply recoat to accessible inner barrel",
  "Cure check",
  "Final inspection",
],
"Strip and Shine - Lift": [
  "Pre-wash inspection",
  "Lift vehicle",
  "Mask brake calipers",
  "Strip old dye/coating from wheel face",
  "Strip from outer barrel",
  "Decontaminate wheel face + outer barrel",
  "Surface prep",
  "Apply recoat to wheel face",
  "Apply recoat to outer barrel",
  "Cure check",
  "Lower vehicle",
  "Final inspection",
],
"Strip and Shine - Wheels Off": [
  "Pre-wash inspection",
  "Lift vehicle",
  "Remove wheels",
  "Mask tires + valve stems",
  "Strip old dye/coating — wheel face",
  "Strip — outer barrel",
  "Strip — inner barrel",
  "Decontaminate full wheel",
  "Surface prep — face",
  "Surface prep — barrels",
  "Apply recoat — face",
  "Apply recoat — outer barrel",
  "Apply recoat — inner barrel",
  "Cure check",
  "Reinstall wheels",
  "Torque check",
  "Lower vehicle",
  "Final inspection",
],
"Paint Correction": [
  "Wash",
  "Clay bar",
  "Buff — front bumper",
  "Buff — rear bumper",
  "Buff — driver fender",
  "Buff — passenger fender",
  "Buff — hood",
  "Buff — roof",
  "Buff — trunk",
  "Buff — driver door",
  "Buff — driver rear door",
  "Buff — passenger door",
  "Buff — passenger rear door",
  "Buff — driver pillar",
  "Buff — passenger pillar",
  "Buff — driver rocker",
  "Buff — passenger rocker",
  "Buff — driver quarter panel",
  "Buff — passenger quarter panel",
  "Buff — driver mirror",
  "Buff — passenger mirror",
  "Polish — front bumper",
  "Polish — rear bumper",
  "Polish — driver fender",
  "Polish — passenger fender",
  "Polish — hood",
  "Polish — roof",
  "Polish — trunk",
  "Polish — driver door",
  "Polish — driver rear door",
  "Polish — passenger door",
  "Polish — passenger rear door",
  "Polish — driver pillar",
  "Polish — passenger pillar",
  "Polish — driver rocker",
  "Polish — passenger rocker",
  "Polish — driver quarter panel",
  "Polish — passenger quarter panel",
  "Polish — driver mirror",
  "Polish — passenger mirror",
  "Final inspection",
],
// ── Services that used to fall through to "Job started / Job complete" ────
// (2026-10-06). Every catalog service that is real bay work now has a list.
// PPF coverage per size is the website's: small = bumper + partial hood +
// headlights + mirrors; medium adds fenders; large is the full front clip.
// ⚠️ PPF install lines say "Install film —", NOT "Buff —/Polish —", so
// tasksForServiceAndVehicle's panel rebuild leaves them alone.
"PPF Front End - Small Vehicle": [
  "Wash",
  "Clay bar",
  "IPA wipe-down",
  "Cut / plot film",
  "Install film — front bumper",
  "Install film — partial hood",
  "Install film — headlights",
  "Install film — driver mirror",
  "Install film — passenger mirror",
  "Wrap & seal edges",
  "Post-heat edges",
  "Final inspection",
],
"PPF Front End - Medium Vehicle": [
  "Wash",
  "Clay bar",
  "IPA wipe-down",
  "Cut / plot film",
  "Install film — front bumper",
  "Install film — partial hood",
  "Install film — driver fender",
  "Install film — passenger fender",
  "Install film — headlights",
  "Install film — driver mirror",
  "Install film — passenger mirror",
  "Wrap & seal edges",
  "Post-heat edges",
  "Final inspection",
],
"PPF Front End - Large Vehicle": [
  "Wash",
  "Clay bar",
  "IPA wipe-down",
  "Cut / plot film",
  "Install film — front bumper",
  "Install film — full hood",
  "Install film — driver fender",
  "Install film — passenger fender",
  "Install film — headlights",
  "Install film — driver mirror",
  "Install film — passenger mirror",
  "Wrap & seal edges",
  "Post-heat edges",
  "Final inspection",
],
"Wheel Coating": [
  "Wash wheels",
  "Iron decon — wheel faces",
  "Surface prep — wheel faces",
  "Coat wheel faces",
  "Cure check",
  "Final inspection",
],
"Wheels Removed - Full Barrel Coating": [
  "Lift vehicle",
  "Remove wheels",
  "Wash wheels — face + barrels",
  "Iron decon — full wheel",
  "Surface prep — face",
  "Surface prep — barrels",
  "Coat — face",
  "Coat — outer barrel",
  "Coat — inner barrel",
  "Cure check",
  "Reinstall wheels",
  "Torque check",
  "Lower vehicle",
  "Final inspection",
],
"Glass Coating": [
  "Clean glass",
  "Glass decon / polish",
  "IPA wipe-down",
  "Coat windshield",
  "Coat side & rear glass",
  "Cure check",
  "Final inspection",
],
"Windshield Only": [
  "Clean windshield",
  "Glass decon / polish",
  "IPA wipe-down",
  "Coat windshield",
  "Cure check",
  "Final inspection",
],
"Headlight Restoration": [
  "Mask surrounding paint & trim",
  "Wet sand headlights",
  "Compound & polish headlights",
  "Apply UV protection",
  "Final inspection",
],
"Clay Bar": [
  "Clay bar",
],
"Odor Bomb": [
  "Odor bomb",
  "Air out cabin",
],
// Mechanical menu (2026-10-06) — see MECH_MENU in lib/constants. The
// "return visit" lines are the mechanic's note that a proper transmission
// service takes 3 visits and a brake/suspension job at least 2; a task is what
// gets the follow-up booked before the car leaves.
"Oil + Filter Service": [
  "Drain oil",
  "Replace oil filter",
  "Refill oil",
  "Check level & leaks",
],
"Transmission Service": [
  "Drain & fill transmission fluid",
  "Replace filter (if customer requested)",
  "Test drive",
  "Book return visit (3 visits total for a proper service)",
],
"Engine / Cabin Air Filter": [
  "Replace engine air filter (if requested)",
  "Replace cabin air filter (if requested)",
],
"Tire / Wheel Service": [
  "Tire / wheel work per request",
  "Torque check",
],
"Brake / Suspension Service": [
  "Brake / suspension work per request",
  "Test drive",
  "Book return visit (at least one more for a proper service)",
],
"General Repair": [
  "Repair per work order",
  "Test drive",
],
"General Diagnostic": [
  "Diagnose",
  "Write up findings for customer",
],
// Winter Menu (season 2026-27).
"Winter Exterior + Iron Decon": [
  "Wheels",
  "Undercarriage rinse",
  "Touchless wash",
  "Contact wash",
  "Iron decon",
  "Coating",
  "Dry",
  "Door jams",
  "Once over",
],
"Salt-Off Express": [
  "Undercarriage salt flush",
  "Wheel wells",
  "Touchless wash",
  "Dry",
  "Once over",
],
"Salt-Off Plus": [
  "Undercarriage salt flush",
  "Wheel wells",
  "Wheels",
  "Touchless wash",
  "Contact wash",
  "Dry",
  "Door jams",
  "Mats",
  "Once over",
],
"Salt-Stain Reset": [
  "Gather and tag personal belongings",
  "Vacuum",
  "Salt-stain treatment — carpets",
  "Salt-stain treatment — mats",
  "Extraction",
  "Door jams",
  "Final vacuum",
  "Put personal belongings back",
],
};

export const DEFAULT_CHECKLIST = ["Job started", "Job complete"];


// Returns the merged, de-duplicated task list for a (possibly comma-separated)
// service string. Mirrors gas/Checklists.js createChecklist's selection logic.
// (Bay-divider grouping from the legacy version is omitted — task SET is identical.)
export function tasksForService(service) {
  const services = String(service || '').split(',').map((s) => s.trim()).filter(Boolean);
  const seen = {};
  const tasks = [];
  services.forEach((svc) => {
    let t = SERVICE_CHECKLISTS[svc] || [];
    if (!t.length) {
      const l = svc.toLowerCase();
      // Parent services first: sized lines ("New Parent Prep – Sedan") miss
      // the exact-key lookup above and must not fall into a looser branch.
      if (l.includes('backseat overhaul')) t = SERVICE_CHECKLISTS['Backseat Overhaul'] || [];
      else if (l.includes('new parent prep')) t = SERVICE_CHECKLISTS['New Parent Prep'] || [];
      else if (l.includes('strip and shine')) {
        if (l.includes('wheels off')) t = SERVICE_CHECKLISTS['Strip and Shine - Wheels Off'] || [];
        else if (l.includes('lift')) t = SERVICE_CHECKLISTS['Strip and Shine - Lift'] || [];
        else t = SERVICE_CHECKLISTS['Strip and Shine - Ground'] || [];
      // PPF: size from the line; the bare "PPF Front End" is the medium price.
      } else if (l.includes('ppf')) {
        if (l.includes('small')) t = SERVICE_CHECKLISTS['PPF Front End - Small Vehicle'] || [];
        else if (l.includes('large')) t = SERVICE_CHECKLISTS['PPF Front End - Large Vehicle'] || [];
        else t = SERVICE_CHECKLISTS['PPF Front End - Medium Vehicle'] || [];
      }
      // Wheel / glass coatings MUST come before the generic 'ceramic' branch:
      // "Wheel Faces Ceramic Coated" and "Glass Ceramic Coating" used to land
      // on the full paint-coating checklist.
      else if (l.includes('barrel') || l.includes('wheels removed') || l.includes('wheels off')) t = SERVICE_CHECKLISTS['Wheels Removed - Full Barrel Coating'] || [];
      else if (l.includes('wheel')) t = SERVICE_CHECKLISTS['Wheel Coating'] || [];
      else if (l.includes('windshield')) t = SERVICE_CHECKLISTS['Windshield Only'] || [];
      else if (l.includes('glass')) t = SERVICE_CHECKLISTS['Glass Coating'] || [];
      else if (l.includes('salt-off plus')) t = SERVICE_CHECKLISTS['Salt-Off Plus'] || [];
      else if (l.includes('salt-off')) t = SERVICE_CHECKLISTS['Salt-Off Express'] || [];
      else if (l.includes('salt-stain') || l.includes('salt stain')) t = SERVICE_CHECKLISTS['Salt-Stain Reset'] || [];
      else if (l.includes('iron decon')) t = SERVICE_CHECKLISTS['Winter Exterior + Iron Decon'] || [];
      // Gentleman's Spec uses the same checklist as Signature.
      else if (l.includes('gentleman') || l.includes('signature') || l.includes('advanced')) t = SERVICE_CHECKLISTS['Signature Service'] || [];
      else if (l.includes('interior')) t = SERVICE_CHECKLISTS['Interior Detail'] || [];
      else if (l.includes('exterior')) t = SERVICE_CHECKLISTS['Exterior Detail'] || [];
      // Seasonal Ceramic MUST come before the generic 'ceramic' branch — and
      // before 'pro': "Winter Seasonal Ceramic (winter promo)" contains "pro".
      else if (l.includes('seasonal') || l.includes('s.c.c')) t = SERVICE_CHECKLISTS['Seasonal Ceramic Coating'] || [];
      // Whole word only — a bare includes('pro') also matched "promo".
      else if (/\bpro\b/.test(l)) t = SERVICE_CHECKLISTS['Pro Service'] || [];
      // 5Y.2S / 1Y.1S are the ceramic coating's catalog names.
      else if (l.includes('ceramic') || l.includes('5y.2s') || l.includes('1y.1s')) t = SERVICE_CHECKLISTS['Ceramic Coating'] || [];
      else if (l.includes('paint correction') || l.includes('2-step')) t = SERVICE_CHECKLISTS['2-Step Paint Correction'] || [];
      else if (l.includes('paint')) t = SERVICE_CHECKLISTS['Paint Correction'] || [];
      else if (l.includes('avant') || l.includes('1051')) t = SERVICE_CHECKLISTS['Avant Guard Formula 1051'] || [];
      // Renamed "Ex. & Wipe" -> "Extraction +" (Ryder, 2026-09-01). The old
      // spellings stay in the matcher: bookings, invoices and Drive PDFs from
      // before the rename still carry them, and an unmatched service name
      // yields an EMPTY checklist with no error at all.
      else if (l.includes('extraction +') || l.includes('extraction+')
            || l.includes('ex. & wipe') || l.includes('ex & wipe')) t = SERVICE_CHECKLISTS['Extraction +'] || [];
      else if (l.includes('extraction')) t = SERVICE_CHECKLISTS['Extraction'] || [];
      else if (l.includes('trim')) t = SERVICE_CHECKLISTS['Trim & Plastic Restoration'] || [];
      else if (l.includes('headlight')) t = SERVICE_CHECKLISTS['Headlight Restoration'] || [];
      else if (l.includes('clay')) t = SERVICE_CHECKLISTS['Clay Bar'] || [];
      else if (l.includes('odor')) t = SERVICE_CHECKLISTS['Odor Bomb'] || [];
    }
    t.forEach((task) => { if (!seen[task]) { seen[task] = true; tasks.push(task); } });
  });
  return tasks.length ? tasks : DEFAULT_CHECKLIST.slice();
}

// ===========================================================================
// PANEL-AWARE CHECKLISTS (coating / paint correction)
// ---------------------------------------------------------------------------
// For per-panel services (ceramic coating, paint correction, seasonal ceramic,
// Avant Guard 1051) the panels worked depend on the body style, not a fixed
// 4-door-sedan list. We infer the body type from the make/model (size field as
// a fallback) and build the panel list to match — a coupe has no rear doors, a
// truck has a bed + tailgate instead of a trunk, an SUV has a liftgate, etc.
// It's a best-effort guess; staff can add/remove panels on the checklist.
// ===========================================================================

// Body type from a "year make model" string, with the pricing-size field as a
// hint when the model isn't recognized. Best-effort keyword match.
export function inferBodyType(desc, size) {
  const s = String(desc || '').toLowerCase();
  const sz = String(size || '').toLowerCase();
  const has = (...words) => words.some((w) => s.includes(w));

  if (has('convertible', 'cabriolet', 'roadster', 'spyder', 'spider', 'droptop', 'soft top')) return 'convertible';
  if (has('f-150', 'f150', 'f-250', 'f250', 'f-350', 'f350', 'f-450', 'silverado', 'sierra', 'ram 1500', 'ram 2500', 'ram 3500', '1500', '2500', '3500', 'tacoma', 'tundra', 'frontier', 'ranger', 'colorado', 'canyon', 'ridgeline', 'gladiator', 'titan', 'maverick', 'dakota', 'avalanche', 'lightning', 'cybertruck', 'r1t', 'pickup', 'truck')) return 'truck';
  if (has('odyssey', 'sienna', 'pacifica', 'carnival', 'sedona', 'transit', 'promaster', 'express', 'savana', 'sprinter', 'metris', 'caravan', 'voyager', 'nv200', 'minivan', ' van')) return 'van';
  if (has('hatchback', 'hatch', 'golf', 'gti', 'veloster', ' fit', 'yaris', 'rallye')) return 'hatchback';
  if (has('wagon', 'outback', 'allroad', 'alltrack', 'estate')) return 'suv';
  if (has('suv', '4runner', '4 runner', 'tahoe', 'suburban', 'yukon', 'expedition', 'explorer', 'pilot', 'passport', 'highlander', 'telluride', 'palisade', 'sorento', 'sportage', 'cr-v', 'crv', 'hr-v', 'hrv', 'rav4', 'rav-4', 'equinox', 'traverse', 'blazer', 'escape', 'edge', 'bronco', 'rogue', 'murano', 'pathfinder', 'armada', 'cx-5', 'cx-9', 'cx-30', 'cx-50', 'cx-90', 'forester', 'ascent', 'wrangler', 'grand cherokee', 'cherokee', 'compass', 'renegade', 'durango', 'atlas', 'tiguan', 'q5', 'q7', 'q8', 'x3', 'x5', 'x7', 'glc', 'gle', 'gls', ' rx', ' nx', ' gx', ' lx', 'mdx', 'rdx', 'qx', 'model x', 'model y', 'ev6', 'ioniq 5', 'mach-e', 'mache', 'kona', 'seltos', ' soul', 'encore', 'enclave', 'acadia', 'terrain', 'trailblazer', 'trax', 'venza', 'defender', 'discovery', 'range rover', 'velar', 'evoque')) return 'suv';
  if (has('coupe', 'mustang', 'camaro', 'corvette', 'challenger', ' 86', 'gr86', 'brz', 'supra', 'miata', 'mx-5', '370z', '350z', '400z', 'gt-r', 'gtr', ' m2', ' m4', 'rc 350', 'lc 500', '8 series', '911', 'cayman', 'boxster', 'gt500', 'gt350', '2-door', '2 door', 'two door')) return 'coupe';

  if (sz.includes('coupe')) return 'coupe';
  if (sz.includes('sedan')) return 'sedan';
  if (sz.includes('hatchback') || sz.includes('midsize suv') || sz.includes('full size suv')) return 'suv';
  if (sz.includes('half ton') || sz.includes('one ton') || sz.includes('van')) return 'truck';
  return 'sedan';
}

// NOTE: this list is what tasksForServiceAndVehicle REBUILDS every panel
// checklist from — the static SERVICE_CHECKLISTS blocks above are only used to
// detect which passes a service runs, so a panel missing HERE is missing from
// every buff/polish job even when the static template lists it. That's what hid
// headlights / pillars / taillights until 2026-08-06.
const _PANEL_FRONT = ['front bumper', 'hood', 'driver fender', 'passenger fender'];
const _PANEL_ROCKERS = ['driver rocker', 'passenger rocker'];
function _panelsForBody(body) {
  const roof = body === 'convertible' ? [] : ['roof'];
  let doors, rearSides, rearClosure;
  switch (body) {
    case 'coupe':
    case 'convertible':
      doors = ['driver door', 'passenger door'];
      rearSides = ['driver quarter panel', 'passenger quarter panel'];
      rearClosure = ['trunk']; break;
    case 'truck':
      doors = ['driver door', 'driver rear door', 'passenger door', 'passenger rear door'];
      rearSides = ['driver bed side', 'passenger bed side'];
      rearClosure = ['tailgate']; break;
    case 'van':
      doors = ['driver door', 'passenger door', 'driver sliding door', 'passenger sliding door'];
      rearSides = ['driver quarter panel', 'passenger quarter panel'];
      rearClosure = ['liftgate']; break;
    case 'suv':
    case 'hatchback':
    case 'wagon':
      doors = ['driver door', 'driver rear door', 'passenger door', 'passenger rear door'];
      rearSides = ['driver quarter panel', 'passenger quarter panel'];
      rearClosure = ['liftgate']; break;
    case 'sedan':
    default:
      doors = ['driver door', 'driver rear door', 'passenger door', 'passenger rear door'];
      rearSides = ['driver quarter panel', 'passenger quarter panel'];
      rearClosure = ['trunk']; break;
  }
  // Pillars and mirrors are per-SIDE — they were one task each, which asked the
  // detailer to tick off both sides of the car in a single check. Everything
  // else that comes in a pair here is already split this way (fenders, doors,
  // rockers, quarter panels), so these two were the odd ones out. Pillars split
  // 2026-09-25, mirrors 2026-09-29. Headlights, taillights and door cups stay
  // single: nobody asked, and a checklist grows in value from being read, not
  // from being long.
  return [..._PANEL_FRONT, 'headlights', ...roof, ...doors, 'door cups',
    'driver pillar', 'passenger pillar', ..._PANEL_ROCKERS,
    ...rearSides, ...rearClosure, 'rear bumper', 'taillights',
    'driver mirror', 'passenger mirror'];
}
export function panelsForVehicle(desc, size) { return _panelsForBody(inferBodyType(desc, size)); }

// Like tasksForService, but for per-panel services it rebuilds the Buff/Polish
// passes from the vehicle's actual panels. Non-panel services pass through.
// vehicle = { desc: "year make model", size: pricing-size string }.
export function tasksForServiceAndVehicle(service, vehicle) {
  const base = tasksForService(service);
  const panelRe = /^(buff|polish)\s*[—-]\s*/i;
  if (!base.some((t) => panelRe.test(t))) return base; // not a panel service
  const firstIdx = base.findIndex((t) => panelRe.test(t));
  let lastIdx = firstIdx;
  base.forEach((t, i) => { if (panelRe.test(t)) lastIdx = i; });
  const prep = base.slice(0, firstIdx);
  const finish = base.slice(lastIdx + 1);
  const passes = [];
  base.slice(firstIdx, lastIdx + 1).forEach((t) => {
    const m = t.match(panelRe);
    if (m) { const v = m[1].toLowerCase() === 'buff' ? 'Buff' : 'Polish'; if (!passes.includes(v)) passes.push(v); }
  });
  const v = vehicle || {};
  const panels = panelsForVehicle(v.desc, v.size);
  const out = [...prep];
  passes.forEach((p) => panels.forEach((pan) => out.push(p + ' — ' + pan)));
  out.push(...finish);
  return out;
}
